import { create } from "zustand";
import { persist } from "zustand/middleware";
import axios from "axios";

import type { CurrentUser } from "@/types/api";

const BASE = import.meta.env.VITE_API_BASE ?? "/api/v1";

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: CurrentUser | null;
  login: (username: string, password: string) => Promise<void>;
  refresh: () => Promise<string | null>;
  loadUser: () => Promise<void>;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,

      async login(username, password) {
        const { data } = await axios.post(`${BASE}/auth/login`, { username, password });
        set({ accessToken: data.access, refreshToken: data.refresh });
        await get().loadUser();
      },

      async refresh() {
        const token = get().refreshToken;
        if (!token) return null;
        try {
          const { data } = await axios.post(`${BASE}/auth/refresh`, { refresh: token });
          set({ accessToken: data.access, refreshToken: data.refresh ?? token });
          return data.access as string;
        } catch {
          set({ accessToken: null, refreshToken: null, user: null });
          return null;
        }
      },

      async loadUser() {
        const token = get().accessToken;
        if (!token) return;
        const { data } = await axios.get(`${BASE}/profile/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        set({ user: data });
      },

      logout() {
        set({ accessToken: null, refreshToken: null, user: null });
      },
    }),
    { name: "mcc-auth", partialize: (s) => ({ accessToken: s.accessToken, refreshToken: s.refreshToken }) },
  ),
);
